import React from 'react';
import { Trans, withTranslation, WithTranslation } from 'react-i18next';
import styled from 'styled-components/macro';

import { H1, P } from '../../../../components/Typography';
import Modal from '../../../../components/Modal';
import { Button, ButtonExternalLink, ButtonGroup } from '../../../../components/Buttons';
import { distances, grid } from '../../../../styles/constants';
import { PayExModalState, State as PayExState } from '../reducer';
import { LoadingOverlay } from '../../../../components/Loading';
import { AccountIdsProps, withAccountIds } from '../../../../auth/accessToken/withAccountIds';
import { Account, PayExConnectionSignup } from '../../../../types/management-auth';
import { Formik } from 'formik';
import { parsePhoneNumber } from 'libphonenumber-js';
import { PayExConnectionSignupContact } from '../../../../types/management-auth/generated';
import { getPartnerConfiguration } from '../../common/partnerConfiguration';
import { CleaveOptions } from 'cleave.js/options';
import { getValueAt } from '../../../../helpers/getValueAt';
import { FormattedInput, Input, ValidationState } from '../../../../components/Forms';
import AccountNumber from '../../../../components/Forms/AccountNumber';
import { getAccountNumberValidator, validate, validateRequired } from '../../../../helpers/validation';

interface ValidatedInputProps extends WithTranslation {
    values: any;
    initialValues: any;
    errors: any;
    path: string;
    onChange: (e: React.FormEvent<HTMLInputElement>) => void;
    onBlur: (e: React.FormEvent<HTMLInputElement>) => void;
    touched: any;
    required?: boolean;
    prefix?: string;
    options?: CleaveOptions;
    maxlength?: number;
    formatter?: (value: string) => string;
    setFieldValue?: (field: string, value: string) => void;
    setFieldTouched?: (field: string) => void;
    country?: string;
}

const ValidatedInput = (
    {
        t,
        values,
        initialValues,
        errors,
        path,
        onChange,
        onBlur,
        touched,
        required,
        prefix,
        options,
        maxlength,
        setFieldValue,
        setFieldTouched,
        country,
    }: ValidatedInputProps
) => {
    const value = getValueAt(path, values);
    const initialValue = getValueAt(path, initialValues);
    const error = getValueAt(path, errors);
    const was_touched = getValueAt(path, touched);
    const validation =
        (was_touched || initialValue) && error
            ? {
                state: ValidationState.Invalid,
                message: error,
            }
            : undefined;
    const label = required
        ? `${t(`settings.payment_connections.collector_modal.fields.${path}`)} * `
        : t(`settings.payment_connections.collector_modal.fields.${path}`);
    if (path === 'account_number' && setFieldValue && setFieldTouched) {
        return (
            <InputWrapper>
                <AccountNumber
                    name={path}
                    value={value || ''}
                    setFieldValue={setFieldValue}
                    onBlur={() => {
                        setFieldTouched('account_number');
                    }}
                    validation={was_touched && validation}
                    country={country || ''}
                    required={required}
                />
            </InputWrapper>
        );
    }

    if (options) {
        return (
            <InputWrapper>
                <FormattedInput
                    label={label}
                    placeholder={t(`settings.payment_connections.collector_modal.placeholders.${path}`)}
                    type="text"
                    name={path}
                    value={value || ''}
                    onChange={(e) => {
                        onChange(e);
                    }}
                    onBlur={(e) => {
                        onChange(e);
                        onBlur(e);
                    }}
                    validation={validation}
                    prefix={prefix}
                    options={options}
                />
            </InputWrapper>
        );
    }
    return (
        <InputWrapper>
            <Input
                label={label}
                placeholder={t(`settings.payment_connections.collector_modal.placeholders.${path}`)}
                type="text"
                name={path}
                value={value || ''}
                onChange={(e) => {
                    onChange(e);
                }}
                onBlur={(e) => {
                    onChange(e);
                    onBlur(e);
                }}
                validation={validation}
                prefix={prefix}
                maxlength={maxlength}
            />
        </InputWrapper>
    );
};

const TranslatedValidatedInput = withTranslation()(ValidatedInput);

const splitApplicantPhoneNumber = (phoneNumber: string | undefined) => {
    if (phoneNumber) {
        try {
            const parsed = parsePhoneNumber(phoneNumber);
            if (parsed) {
                return [`+${parsed.countryCallingCode}`, parsed.formatNational() as string];
            }
        } catch {
            // ingnore errors
        }
    }
    return ['+47', ''];
};

const nameOrEmpty = (account: Account) => {
    if (account && account.applicant && account.applicant.first_name && account.applicant.last_name) {
        return `${account.applicant.first_name} ${account.applicant.last_name}`;
    }
    return '';
};

export interface PayExModalProps extends WithTranslation, AccountIdsProps {
    initiatePayExConnection: (accountId: string, data: PayExConnectionSignup, formUrl: string) => void;
    closePayExModal: () => void;
    openPayexForm: () => void;
    payex: PayExState;
}

type ContactWithCountry = PayExConnectionSignupContact & {
    country_prefix: string;
};

interface FormikValueProps {
    applicant_contact: ContactWithCountry;
    accounting_contact: ContactWithCountry;
    bank_name: string;
    account_number: string;
    business_name: string;
    legal_name: string;
    [key: string]: any;
}
const PayExModal = (
    {
        t,
        payex,
        closePayExModal,
        initiatePayExConnection,
        prodAccountId,
        openPayexForm,
    }: PayExModalProps
) => {
    if (!prodAccountId) {
        return null;
    }

    if (payex.payexModalState === PayExModalState.Closed) {
        return null;
    }

    const account = payex.account;
    if (!account) {
        return <LoadingOverlay />;
    }

    const [country_prefix, phone_number] = splitApplicantPhoneNumber(account ? account.applicant.phone_number : '');
    const initialFormValues: FormikValueProps = {
        applicant_contact: {
            name: nameOrEmpty(account),
            email: account.applicant.email || '',
            phone_number,
            country_prefix,
        },
        accounting_contact: {
            name: nameOrEmpty(account),
            email: account.billing.email || '',
            phone_number: phone_number,
            country_prefix: country_prefix,
        },
        bank_name: '',
        account_number: '',
        business_name: account.company ? account.company.business_name : '',
        legal_name: account.company ? account.company.business_name : '',
    };

    const validateValues = (values: FormikValueProps) => {
        let errors: { [key: string]: string } = {};
        if (!values) {
            // this should not happen but...
            return { error: 'account not loaded yet...' };
        }
        const validators = [
            {
                path: 'bank_name',
                validator: validateRequired(t('settings.payment_connections.collector_modal.invalid.bank_name')),
            },
            {
                path: 'account_number',
                validator: validateRequired(
                    t('settings.payment_connections.collector_modal.invalid.account_number.required')
                ),
            },
            {
                path: 'account_number',
                validator: getAccountNumberValidator(
                    account?.company.address.country ?? '',
                    t('settings.payment_connections.collector_modal.invalid.account_number.required')
                ),
            },
        ];
        errors = validators.reduce((acc, elem) => {
            return validate(elem.path, elem.validator, values, acc);
        }, errors);
        return errors;
    };

    if (!account) {
        return <LoadingOverlay />;
    }
    const initialValidation = validateValues(initialFormValues);
    return (
        (<Modal fixedWidth>
            {[PayExModalState.ApplicationForm, PayExModalState.InitiateApplicationLoading].includes(
                payex.payexModalState
            ) && (
                <>
                    <H1>{t('settings.payment_connections.payex_modal.title_intro')}</H1>
                    <Formik
                        enableReinitialize
                        initialValues={initialFormValues}
                        initialErrors={initialValidation}
                        validate={validateValues}
                        onSubmit={(values) => {
                            if (
                                !account ||
                                !account.company ||
                                !account.company.organization_number ||
                                !account.company.website ||
                                !prodAccountId
                            ) {
                                // How could we ever get here?
                                return;
                            }
                            const formUrl =
                                getPartnerConfiguration(prodAccountId, account.company.address.country)
                                    ?.payment_product_types['payex.creditcard']?.link || '';
                            initiatePayExConnection(
                                prodAccountId,
                                {
                                    account_number: values.account_number.replace(/\s/g, ''),
                                    business_name: values.business_name,
                                    legal_name: values.legal_name,
                                    organization_number: account.company.organization_number || '',
                                    bank_name: values.bank_name,
                                    website: account.company.website || '',
                                    applicant_contact: {
                                        name: values.applicant_contact.name,
                                        email: values.applicant_contact.email,
                                        phone_number: values.applicant_contact.phone_number,
                                    },
                                    accounting_contact: {
                                        name: values.accounting_contact.name,
                                        email: values.accounting_contact.email,
                                        phone_number: values.accounting_contact.phone_number,
                                    },
                                    payment_options: [
                                        {
                                            currencies: ['NOK'],
                                            type: 'payex.creditcard',
                                        },
                                    ],
                                },
                                formUrl
                            );
                        }}
                    >
                        {({
                            handleSubmit,
                            isValid,
                            values,
                            handleBlur,
                            handleChange,
                            errors,
                            setFieldValue,
                            setFieldTouched,
                            touched,
                        }) => {
                            return (
                                <form onSubmit={handleSubmit}>
                                    <SpacedWrapper>
                                        <P>
                                            <Trans i18nKey="settings.payment_connections.payex_modal.intro" />
                                        </P>
                                    </SpacedWrapper>
                                    {!(initialFormValues.bank_name && initialFormValues.account_number) && (
                                        <SpacedWrapper>
                                            <TranslatedValidatedInput
                                                path="bank_name"
                                                initialValues={initialFormValues}
                                                values={values}
                                                errors={errors}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                touched={touched}
                                                required
                                            />
                                            <TranslatedValidatedInput
                                                path="account_number"
                                                initialValues={initialFormValues}
                                                values={values}
                                                errors={errors}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                touched={touched}
                                                country={account.company.address.country}
                                                setFieldValue={setFieldValue}
                                                setFieldTouched={setFieldTouched}
                                                required
                                            />
                                        </SpacedWrapper>
                                    )}
                                    <ButtonGroup>
                                        <Button type="submit" disabled={!isValid}>
                                            {t(
                                                'settings.payment_connections.payex_modal.sign_up_with_payex_call_to_action'
                                            )}
                                        </Button>
                                        <Button className="alt" onClick={closePayExModal}>
                                            {t('settings.payment_connections.payex_modal.cancel')}
                                        </Button>
                                    </ButtonGroup>
                                    {payex.payexModalState === PayExModalState.InitiateApplicationLoading && (
                                        <LoadingOverlay />
                                    )}
                                </form>
                            );
                        }}
                    </Formik>
                </>
            )}
            {payex.payexModalState === PayExModalState.ShowFormLink && (
                <>
                    <H1>{t('settings.payment_connections.payex_modal.title_open_form')}</H1>

                    <SpacedWrapper>
                        <P>{t('settings.payment_connections.payex_modal.open_form')}</P>
                    </SpacedWrapper>

                    <ButtonGroup>
                        <ButtonExternalLink
                            href={payex.formUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={openPayexForm}
                        >
                            {t('settings.payment_connections.payex_modal.open_form_call_to_action')}
                        </ButtonExternalLink>
                    </ButtonGroup>
                    <SpacedWrapper>
                        <br />
                        <small>
                            <Trans i18nKey="settings.payment_connections.payex_modal.open_form_more" />
                        </small>
                    </SpacedWrapper>
                </>
            )}
            {payex.payexModalState === PayExModalState.ApplicationReceived && (
                <>
                    <H1>{t('settings.payment_connections.payex_modal.title_last_step')}</H1>

                    <SpacedWrapper>
                        <Trans i18nKey="settings.payment_connections.payex_modal.last_step">
                            <P className="margin-bottom">
                                You will be able to accept Visa and Mastercard payments once PayEx/Swedbank has accepted
                                your application.
                            </P>
                            <P>
                                In some cases PayEx/Swedbank might reach out to you with some additional questions about
                                the nature of your business before the application is approved.
                            </P>
                        </Trans>
                    </SpacedWrapper>

                    <ButtonGroup>
                        <Button onClick={closePayExModal}>{t('settings.payment_connections.payex_modal.close')}</Button>
                    </ButtonGroup>
                </>
            )}
        </Modal>)
    );
};

export default withAccountIds(withTranslation()(PayExModal));

const SpacedWrapper = styled.div`
    margin-top: ${distances.small};
    margin-bottom: ${distances.normal};
`;

const InputWrapper = styled.div`
    margin-right: ${grid.gutter};

    &:last-child {
        margin: 0;
    }
`;
